import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, Phone } from "lucide-react";
import SiteLayout from "@/components/layout/SiteLayout";
import PageHero from "@/components/sections/PageHero";
import ContentSection from "@/components/sections/ContentSection";
import CTABlock from "@/components/sections/CTABlock";
import estherPortrait from "@/assets/esther-headshot.jpg";
import heroImage from "@/assets/bg-exterior-1.jpg";

const InstagramIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3.5" y="3.5" width="17" height="17" rx="4.5" />
    <circle cx="12" cy="12" r="3.75" />
    <circle cx="17.5" cy="6.5" r="0.75" fill="currentColor" stroke="none" />
  </svg>
);

const FacebookIcon = ({ className = "h-4 w-4" }: { className?: string }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={className} fill="currentColor">
    <path d="M13.5 21v-7h2.35l.4-3h-2.75V9.08c0-.87.24-1.46 1.5-1.46H16.4V4.94c-.24-.03-1.06-.1-2.02-.1-2 0-3.38 1.22-3.38 3.47V11H8.75v3H11v7h2.5Z" />
  </svg>
);

const TeamEsther = () => {
  useEffect(() => {
    document.title = "Esther Summersett | Hadassah Homes";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Meet Esther Summersett, Broker-Owner and Founder of Hadassah Homes. Real estate professional, interior designer, and investor serving Massachusetts and the Dominican Republic.");
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
    canonical.href = "https://hadassahhomesinc.com/team/esther-summersett";
  }, []);

  return (
    <SiteLayout>
      <PageHero
        compact
        eyebrow="Meet the Team"
        title="Esther Summersett"
        backgroundImage={heroImage}
      />

      <ContentSection>
        <div className="flex flex-col md:flex-row gap-12 md:gap-20 items-start">
          <div className="w-full md:w-5/12 shrink-0">
            <div className="aspect-[3/4] overflow-hidden">
              <img
                src={estherPortrait}
                alt="Esther Summersett, Broker-Owner at Hadassah Homes"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
            <div className="mt-6 space-y-3 border-t border-border pt-6">
              <p className="font-body text-[10px] uppercase tracking-[0.3em] text-accent">Contact</p>
              <a href="mailto:Esther@hadassahhomesinc.com" className="flex items-center gap-3 font-body text-sm text-foreground hover:text-accent transition-colors">
                <Mail className="w-4 h-4 text-accent shrink-0" strokeWidth={1.5} />
                Esther@hadassahhomesinc.com
              </a>
              <a href="tel:+17813092010" className="flex items-center gap-3 font-body text-sm text-muted-foreground hover:text-accent transition-colors">
                <Phone className="w-4 h-4 text-accent shrink-0" strokeWidth={1.5} />
                (781) 309-2010
              </a>
              <div className="pt-2 space-y-2">
                <p className="font-body text-[10px] uppercase tracking-[0.3em] text-accent/80">Social</p>
                <div className="flex flex-wrap gap-2">
                  <a
                    href="https://www.facebook.com/esthersummersett"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Esther Summersett on Facebook"
                    title="Facebook"
                    className="inline-flex h-8 w-8 items-center justify-center border border-border/60 text-muted-foreground transition-colors hover:border-accent hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
                  >
                    <FacebookIcon />
                  </a>
                  <a
                    href="https://www.instagram.com/esthersummersettbroker/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Esther Summersett on Instagram"
                    title="Instagram"
                    className="inline-flex h-8 w-8 items-center justify-center border border-border/60 text-muted-foreground transition-colors hover:border-accent hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
                  >
                    <InstagramIcon />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full space-y-6">
            <span className="font-body text-[10px] uppercase tracking-[0.4em] text-accent">Founder</span>
            <h2 className="font-headline text-3xl md:text-5xl text-foreground leading-tight">
              Broker-Owner, Realtor &amp; Interior Designer
            </h2>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Hadassah Homes was founded by Esther Summersett, a real estate professional driven by a passion for helping individuals and families make confident, informed decisions.
            </p>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Esther entered real estate with a clear purpose: to create a better experience for clients who often feel overwhelmed, underserved, or unsure of where to begin. She saw firsthand how many people were making major financial decisions without the guidance and education they truly needed.
            </p>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              With a background in social justice and a strong eye for design and presentation, Esther brings a well-rounded approach to every client interaction. She understands not only how to navigate the Massachusetts real estate market, but how to position a property to stand out and perform.
            </p>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              Her approach is simple: listen first, guide with intention, and deliver with excellence.
            </p>
            <div className="pt-4 flex flex-wrap gap-4">
              <Link
                to="/consultation"
                className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.1em] px-8 py-3.5 bg-accent text-white hover:bg-accent/90 transition-colors"
              >
                Book with Esther <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/team"
                className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.1em] px-8 py-3.5 border border-border text-foreground hover:border-accent hover:text-accent transition-colors"
              >
                Meet the Team
              </Link>
            </div>
          </div>
        </div>
      </ContentSection>

      <CTABlock
        variant="dark"
        title="Ready to Work With Esther?"
        description="Get expert guidance tailored to your goals — buying, selling, investing, or designing."
        primaryCta={{ text: "Book a Consultation →", href: "/consultation" }}
      />
    </SiteLayout>
  );
};

export default TeamEsther;
